import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import ReactLoading from 'react-loading';

import InlineAlert from '../../../utilities/form-components/InlineAlert';
import TabComponent from './TabComponent/TabComponent';
import TitleBar from './TitleBar/TitleBar';
import { getShippingReviewData, updateShippingReviewData, getArtaSubtypes } from '../../../modules/shippingReview/thunks';
import { selectHouseID } from '../../../selectors';

const Step7 = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('All');
  const [showConfirmAllButton, setShowConfirmAllButton] = useState(true);

  const isShippingReviewDataLoading = useSelector((state) => state.shippingReview.isShippingReviewDataLoading);
  const { shippingItemCountAll, needsReviewCount, readyToConfirmCount, inProgressCount, lotsWithShippingData, sessionTitle } = useSelector((state) => state.shippingReview);
  const houseID = useSelector(selectHouseID);

  useEffect(() => {
    const controller = new AbortController();
    dispatch(getArtaSubtypes(controller.signal));
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (needsReviewCount === 0 && readyToConfirmCount === 0) {
      setShowConfirmAllButton(false);
    } else {
      setShowConfirmAllButton(true);
    }
  }, [needsReviewCount, readyToConfirmCount]);

  useEffect(() => {
    const pathParamsArr = window.location.pathname.split('/'); // /session/live-timed/groupID/catalogID/step
    const catalogID = pathParamsArr[pathParamsArr.length - 2];
    if (!catalogID || !houseID) return;
    dispatch(getShippingReviewData(catalogID, houseID));
  }, [houseID, dispatch]);

  const handleConfirmAll = () => {
    const lotsToConfirm = lotsWithShippingData.filter((lot) => lot.shippingReviewData.status === 'awaiting_approval');
    lotsToConfirm.forEach((lot) => {
      const payload = { review: { reviewComplete: true } };
      dispatch(updateShippingReviewData(lot.lotID, lot.shippingReviewData.id, lot.houseID, payload));
    });
  };

  const needsReviewLink = (
    <Button
      key="needs-review"
      color="link"
      className="filter-link-btn ml-1"
      onClick={() => setActiveTab('Needs Review')}
    >
      See Here
    </Button>
  );

  return (
    <>
      {isShippingReviewDataLoading && <ReactLoading className="loading-spinner" data-testid="loading-spinner" type="spin" color="black" />}
      {!isShippingReviewDataLoading && !!shippingItemCountAll && (
        <div className="pb-5 mt-5" data-testid="shipping-review-data-container">
          {(readyToConfirmCount > 0 || inProgressCount > 0) && (
            <InlineAlert
              titleText="Please confirm shipping details below."
              baseText="Shipping details must be confirmed at least 2 hours before the sale starts. Once confirmed, the shipping information cannot be changed."
              color="warning"
              icon="truck"
              showPhones={false}
              data-testid="warning-banner"
            />
          )}
          {(needsReviewCount > 0)
          && (
          <InlineAlert
            baseText={[`${needsReviewCount} ${needsReviewCount === 1 ? 'lot needs' : 'lots need'} review`, needsReviewLink]}
            color="danger"
            icon="exclamation-triangle"
            showPhones={false}
            data-testid="danger-banner"
          />
          )}
          {(readyToConfirmCount === 0) && (needsReviewCount === 0) && (!inProgressCount || inProgressCount === 0)
          && (
          <InlineAlert
            baseText="Your shipping details have been confirmed!"
            color="success"
            icon="check-circle"
            showPhones={false}
            data-testid="success-banner"
          />
          )}
          {sessionTitle && <TitleBar step={7} title="Confirm Shipping Details" sessionName={sessionTitle} onConfirmAll={handleConfirmAll} showConfirmAllButton={showConfirmAllButton} disabled={readyToConfirmCount === 0} />}
          <TabComponent activeTab={activeTab} onClickTab={setActiveTab} data-testid="tab-component" />
        </div>
      )}
      {!isShippingReviewDataLoading && !shippingItemCountAll && (
        <InlineAlert
          baseText="Error loading shipping review data."
          color="danger"
          icon="exclamation-triangle"
          showPhones={false}
        />
      )}
    </>
  );
};

export default withRouter(Step7);
